/**
 * FHIR client interface (PROD-642 Sprint 2.2).
 *
 * Defines the contract every implementation (`LiveAidboxFhirClient`,
 * `DemoFhirClient`) satisfies. Care Pathway Studio reads + writes the
 * canonical Pathway Studio resources (PlanDefinition, ActivityDefinition,
 * Questionnaire, Library) plus the runtime resources used by the rest of
 * the four-layer ADR-001 architecture (CarePlan, Task, QuestionnaireResponse,
 * Observation, Provenance, AuditEvent).
 *
 * Design notes:
 *   - Every read AND write is tenant-scoped. The client asserts the
 *     `tenant-id` extension matches the caller's `tenantId`. A mismatch
 *     throws `TenantMismatchError`.
 *   - Writes that round-trip a versioned resource (`PlanDefinition`,
 *     `ActivityDefinition`, `Questionnaire`, `Library`) accept an
 *     `If-Match` ETag for optimistic concurrency. A 412 from Aidbox
 *     surfaces as `EtagConflictError`.
 *   - 5xx + 429 responses are retried with exponential backoff (3 tries
 *     total: 100ms / 400ms / 1000ms). 4xx other than 412 / 429 fail fast.
 *   - HTTP 410 ("Gone") is treated as 404 per Aidbox quirk
 *     (Confluence ENG/4297621505).
 *   - `PlanDefinition.action` MUST NOT be an empty array. Aidbox FHIR
 *     Schema rejects the resource otherwise. The client guards the write
 *     before issuing the request so the caller gets a clean
 *     `FhirValidationError` rather than a 422 round-trip.
 *
 * HC5: zero em / en dashes anywhere.
 */
import type {
  ActivityDefinition,
  AuditEvent,
  Bundle,
  CarePlan,
  CpsResourceType,
  FhirResource,
  Library,
  Observation,
  PlanDefinition,
  Provenance,
  Questionnaire,
  QuestionnaireResponse,
  SearchParams,
  Task,
} from './types';
import {
  EtagConflictError,
  FhirAuthError,
  FhirNotFoundError,
  FhirTransportError,
  FhirValidationError,
  wrapUnknownError,
} from './errors';
import {
  assertTenantMatches,
  readTenantId,
  withTenantId,
} from './extensions';

/** Public interface every FHIR client implementation satisfies. */
export interface FhirClient {
  /** Read a `PlanDefinition` by id with tenant assertion. */
  getPlanDefinition(id: string, tenantId: string): Promise<PlanDefinition>;
  /** Search `PlanDefinition` resources within a tenant. */
  searchPlanDefinitions(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<PlanDefinition>>;
  createPlanDefinition(
    resource: PlanDefinition,
    tenantId: string,
  ): Promise<PlanDefinition>;
  updatePlanDefinition(
    resource: PlanDefinition,
    tenantId: string,
    ifMatch: string,
  ): Promise<PlanDefinition>;

  getActivityDefinition(
    id: string,
    tenantId: string,
  ): Promise<ActivityDefinition>;
  searchActivityDefinitions(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<ActivityDefinition>>;
  createActivityDefinition(
    resource: ActivityDefinition,
    tenantId: string,
  ): Promise<ActivityDefinition>;
  updateActivityDefinition(
    resource: ActivityDefinition,
    tenantId: string,
    ifMatch: string,
  ): Promise<ActivityDefinition>;

  getQuestionnaire(id: string, tenantId: string): Promise<Questionnaire>;
  searchQuestionnaires(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<Questionnaire>>;
  createQuestionnaire(
    resource: Questionnaire,
    tenantId: string,
  ): Promise<Questionnaire>;
  updateQuestionnaire(
    resource: Questionnaire,
    tenantId: string,
    ifMatch: string,
  ): Promise<Questionnaire>;

  getLibrary(id: string, tenantId: string): Promise<Library>;
  searchLibraries(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<Library>>;
  createLibrary(resource: Library, tenantId: string): Promise<Library>;
  updateLibrary(
    resource: Library,
    tenantId: string,
    ifMatch: string,
  ): Promise<Library>;

  getCarePlan(id: string, tenantId: string): Promise<CarePlan>;
  searchCarePlans(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<CarePlan>>;
  createCarePlan(resource: CarePlan, tenantId: string): Promise<CarePlan>;
  updateCarePlan(
    resource: CarePlan,
    tenantId: string,
    ifMatch: string,
  ): Promise<CarePlan>;

  getTask(id: string, tenantId: string): Promise<Task>;
  searchTasks(tenantId: string, params?: SearchParams): Promise<Bundle<Task>>;
  createTask(resource: Task, tenantId: string): Promise<Task>;
  updateTask(resource: Task, tenantId: string, ifMatch: string): Promise<Task>;

  getQuestionnaireResponse(
    id: string,
    tenantId: string,
  ): Promise<QuestionnaireResponse>;
  searchQuestionnaireResponses(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<QuestionnaireResponse>>;
  createQuestionnaireResponse(
    resource: QuestionnaireResponse,
    tenantId: string,
  ): Promise<QuestionnaireResponse>;

  getObservation(id: string, tenantId: string): Promise<Observation>;
  searchObservations(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<Observation>>;
  createObservation(
    resource: Observation,
    tenantId: string,
  ): Promise<Observation>;

  getProvenance(id: string, tenantId: string): Promise<Provenance>;
  createProvenance(
    resource: Provenance,
    tenantId: string,
  ): Promise<Provenance>;

  getAuditEvent(id: string, tenantId: string): Promise<AuditEvent>;
  createAuditEvent(
    resource: AuditEvent,
    tenantId: string,
  ): Promise<AuditEvent>;
}

/**
 * Aidbox FHIR Schema rejects `PlanDefinition` resources whose `action`
 * is an empty array. We pre-validate here so the caller gets a clean
 * `FhirValidationError` rather than a 422 round-trip.
 */
export function assertPlanDefinitionWritable(resource: PlanDefinition): void {
  if (Array.isArray(resource.action) && resource.action.length === 0) {
    throw new FhirValidationError(
      'PlanDefinition',
      'action MUST NOT be an empty array. Provide at least one action or omit the field. (Aidbox FHIR Schema quirk)',
    );
  }
}

/** Sleep helper for the retry path. Pulled out for test stubability. */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Decide whether a transport failure is worth retrying. */
function isRetryableStatus(status: number): boolean {
  return status === 429 || (status >= 500 && status < 600);
}

/**
 * Default retry budget. Three total attempts (one initial + two retries).
 * The supervisor brief says "3-retry exponential backoff"; we interpret
 * that as 3 total tries (initial + 2 retries) per industry idiom.
 */
const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_BACKOFF_MS = [100, 400, 1000];

export interface RetryOptions {
  maxAttempts?: number;
  backoffMs?: number[];
  /** Override the sleep function (tests). */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Configuration for the live Aidbox client. The base URL is the Aidbox
 * FHIR REST endpoint root (e.g. `https://wolqinjezg.edge.aidbox.app/fhir`).
 * Token endpoint is the Aidbox token endpoint
 * (`https://<host>/auth/token`).
 */
export interface LiveAidboxClientOptions {
  baseUrl: string;
  tokenUrl: string;
  clientId: string;
  clientSecret: string;
  /** Optional fetch override (tests). Defaults to global `fetch`. */
  fetchImpl?: typeof fetch;
  /** Optional clock for token expiry computation (tests). */
  now?: () => number;
  retry?: RetryOptions;
}

/** Token cache entry. The expiry reserves a 5-minute buffer. */
interface TokenCacheEntry {
  accessToken: string;
  /** Epoch ms when the token must be refreshed (5 min before real expiry). */
  refreshAtMs: number;
}

/** Default token TTL (Aidbox returns 3600s; we refresh 5 min early). */
const DEFAULT_TOKEN_TTL_MS = 60 * 60 * 1000; // 1h
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 min

/**
 * Live Aidbox FHIR client. Hits the Aidbox dev instance over HTTPS using
 * client_credentials OAuth. All reads + writes are tenant-scoped via the
 * `tenant-id` extension.
 */
export class LiveAidboxFhirClient implements FhirClient {
  private readonly opts: Required<
    Omit<LiveAidboxClientOptions, 'retry' | 'fetchImpl' | 'now'>
  > & {
    fetchImpl: typeof fetch;
    now: () => number;
    retry: Required<Omit<RetryOptions, 'sleep'>> & {
      sleep: (ms: number) => Promise<void>;
    };
  };

  private tokenCache: TokenCacheEntry | null = null;

  constructor(options: LiveAidboxClientOptions) {
    if (!options.baseUrl) {
      throw new FhirAuthError('LiveAidboxFhirClient requires baseUrl');
    }
    if (!options.clientId || !options.clientSecret) {
      throw new FhirAuthError(
        'LiveAidboxFhirClient requires clientId and clientSecret',
      );
    }
    this.opts = {
      baseUrl: options.baseUrl.replace(/\/$/, ''),
      tokenUrl: options.tokenUrl,
      clientId: options.clientId,
      clientSecret: options.clientSecret,
      fetchImpl: options.fetchImpl ?? globalThis.fetch.bind(globalThis),
      now: options.now ?? (() => Date.now()),
      retry: {
        maxAttempts: options.retry?.maxAttempts ?? DEFAULT_MAX_ATTEMPTS,
        backoffMs: options.retry?.backoffMs ?? DEFAULT_BACKOFF_MS,
        sleep: options.retry?.sleep ?? sleep,
      },
    };
  }

  // --- Authentication ----------------------------------------------------

  /**
   * Fetch (or reuse) a bearer token via the OAuth client_credentials
   * grant. Cached for ~55 minutes (5-minute buffer before the 1h TTL).
   */
  private async authenticate(): Promise<string> {
    const cached = this.tokenCache;
    if (cached && this.opts.now() < cached.refreshAtMs) {
      return cached.accessToken;
    }
    const body = new URLSearchParams({
      grant_type: 'client_credentials',
      client_id: this.opts.clientId,
      client_secret: this.opts.clientSecret,
    });
    let res: Response;
    try {
      res = await this.opts.fetchImpl(this.opts.tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch (err) {
      throw new FhirAuthError(
        'Token endpoint fetch failed',
        err,
      );
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new FhirAuthError(
        `Token endpoint returned ${res.status}: ${text.slice(0, 200)}`,
      );
    }
    const json = (await res.json()) as {
      access_token?: string;
      expires_in?: number;
    };
    if (!json.access_token) {
      throw new FhirAuthError('Token endpoint response missing access_token');
    }
    const ttlMs =
      typeof json.expires_in === 'number' && json.expires_in > 0
        ? json.expires_in * 1000
        : DEFAULT_TOKEN_TTL_MS;
    const refreshAtMs =
      this.opts.now() + Math.max(ttlMs - TOKEN_REFRESH_BUFFER_MS, 0);
    this.tokenCache = {
      accessToken: json.access_token,
      refreshAtMs,
    };
    return json.access_token;
  }

  // --- Transport ---------------------------------------------------------

  /**
   * Issue a request against the FHIR REST endpoint with retry on
   * 5xx + 429. Returns the parsed JSON body on success.
   */
  private async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string,
    init: { body?: unknown; headers?: Record<string, string> } = {},
  ): Promise<{ body: T; etag: string | null }> {
    const url = `${this.opts.baseUrl}${path}`;
    const token = await this.authenticate();

    let lastErr: unknown = null;
    for (let attempt = 0; attempt < this.opts.retry.maxAttempts; attempt++) {
      let res: Response;
      try {
        res = await this.opts.fetchImpl(url, {
          method,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/fhir+json',
            'Content-Type': 'application/fhir+json',
            ...(init.headers ?? {}),
          },
          body:
            init.body !== undefined ? JSON.stringify(init.body) : undefined,
        });
      } catch (err) {
        lastErr = err;
        // Network-level failure: treat as retryable.
        if (attempt < this.opts.retry.maxAttempts - 1) {
          await this.opts.retry.sleep(
            this.opts.retry.backoffMs[attempt] ?? 1000,
          );
          continue;
        }
        throw new FhirTransportError(
          `Network failure for ${method} ${path}`,
          undefined,
          err,
        );
      }

      // 404 + 410 are terminal "not found" - per Aidbox quirk we treat
      // 410 the same as 404.
      if (res.status === 404 || res.status === 410) {
        throw new FhirNotFoundError(
          'unknown',
          path.split('/').pop() ?? '',
          new FhirTransportError(
            `${method} ${path} returned ${res.status}`,
            res.status,
          ),
        );
      }

      // 412 = ETag conflict (PUT with If-Match).
      if (res.status === 412) {
        const ifMatch = init.headers?.['If-Match'] ?? '';
        const lastSlash = path.lastIndexOf('/');
        const id = lastSlash >= 0 ? path.slice(lastSlash + 1) : '';
        const typeStart = path.indexOf('/', 1);
        const resourceType =
          typeStart >= 0
            ? path.slice(1, typeStart === lastSlash ? lastSlash : typeStart)
            : 'unknown';
        throw new EtagConflictError(resourceType, id, ifMatch);
      }

      // 422 = Aidbox FHIR Schema rejection.
      if (res.status === 422) {
        const text = await res.text().catch(() => '');
        throw new FhirValidationError(
          'unknown',
          `${method} ${path} returned 422: ${text.slice(0, 500)}`,
        );
      }

      // Retryable transport failure.
      if (isRetryableStatus(res.status)) {
        lastErr = new FhirTransportError(
          `${method} ${path} returned ${res.status}`,
          res.status,
        );
        if (attempt < this.opts.retry.maxAttempts - 1) {
          await this.opts.retry.sleep(
            this.opts.retry.backoffMs[attempt] ?? 1000,
          );
          continue;
        }
        throw lastErr;
      }

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new FhirTransportError(
          `${method} ${path} returned ${res.status}: ${text.slice(0, 200)}`,
          res.status,
        );
      }

      const etag = res.headers.get('ETag');
      // Aidbox returns 200 with body on PUT and 201 on POST. Both have JSON.
      const body = (await res.json()) as T;
      return { body, etag };
    }
    throw wrapUnknownError(`${method} ${path} exhausted retries`, lastErr);
  }

  // --- Read / search / create / update primitives ------------------------

  private async readResource<T extends FhirResource>(
    resourceType: CpsResourceType,
    id: string,
    tenantId: string,
  ): Promise<T> {
    const { body } = await this.request<T>('GET', `/${resourceType}/${id}`);
    if (body.resourceType !== resourceType) {
      throw new FhirValidationError(
        resourceType,
        `Response resourceType mismatch: ${body.resourceType}`,
      );
    }
    assertTenantMatches(body, tenantId);
    return body;
  }

  private async searchResources<T extends FhirResource>(
    resourceType: CpsResourceType,
    tenantId: string,
    params: SearchParams = {},
  ): Promise<Bundle<T>> {
    const merged: SearchParams = {
      ...params,
      // Aidbox supports filtering by extension via the chained search
      // syntax `extension.url=...` plus `extension.valueString=...`.
      // For our tenant filter we issue a custom param the server-side
      // SearchParameter resolves.
      _tag: undefined,
    };
    const queryParts: string[] = [];
    for (const [k, v] of Object.entries(merged)) {
      if (v === undefined || v === null) continue;
      queryParts.push(
        `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
      );
    }
    queryParts.push(`tenant-id=${encodeURIComponent(tenantId)}`);
    const query = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    const { body } = await this.request<Bundle<T>>(
      'GET',
      `/${resourceType}${query}`,
    );
    // Defense-in-depth: drop entries whose tenant extension does not
    // match. Server-side scope filter is the upstream guard, but the
    // SMART scope evaluator is not yet configured (per supervisor brief).
    if (body.entry) {
      body.entry = body.entry.filter((entry) => {
        if (!entry.resource) return false;
        return readTenantId(entry.resource) === tenantId;
      });
      body.total = body.entry.length;
    }
    return body;
  }

  private async createResource<T extends FhirResource>(
    resourceType: CpsResourceType,
    resource: T,
    tenantId: string,
  ): Promise<T> {
    if (resource.resourceType !== resourceType) {
      throw new FhirValidationError(
        resourceType,
        `Resource has wrong resourceType: ${resource.resourceType}`,
      );
    }
    if (resourceType === 'PlanDefinition') {
      assertPlanDefinitionWritable(resource as unknown as PlanDefinition);
    }
    const tagged = withTenantId(resource, tenantId);
    const { body } = await this.request<T>(
      'POST',
      `/${resourceType}`,
      { body: tagged },
    );
    assertTenantMatches(body, tenantId);
    return body;
  }

  private async updateResource<T extends FhirResource>(
    resourceType: CpsResourceType,
    resource: T,
    tenantId: string,
    ifMatch: string,
  ): Promise<T> {
    if (resource.resourceType !== resourceType) {
      throw new FhirValidationError(
        resourceType,
        `Resource has wrong resourceType: ${resource.resourceType}`,
      );
    }
    if (!resource.id) {
      throw new FhirValidationError(
        resourceType,
        'Update requires an id on the resource',
      );
    }
    if (resourceType === 'PlanDefinition') {
      assertPlanDefinitionWritable(resource as unknown as PlanDefinition);
    }
    // Defense-in-depth: caller asserts tenant before write.
    assertTenantMatches(resource, tenantId);
    const tagged = withTenantId(resource, tenantId);
    const { body } = await this.request<T>(
      'PUT',
      `/${resourceType}/${resource.id}`,
      {
        body: tagged,
        headers: { 'If-Match': ifMatch },
      },
    );
    assertTenantMatches(body, tenantId);
    return body;
  }

  // --- Public surface (delegates) ---------------------------------------

  getPlanDefinition(id: string, tenantId: string): Promise<PlanDefinition> {
    return this.readResource('PlanDefinition', id, tenantId);
  }
  searchPlanDefinitions(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<PlanDefinition>> {
    return this.searchResources('PlanDefinition', tenantId, params);
  }
  createPlanDefinition(
    resource: PlanDefinition,
    tenantId: string,
  ): Promise<PlanDefinition> {
    return this.createResource('PlanDefinition', resource, tenantId);
  }
  updatePlanDefinition(
    resource: PlanDefinition,
    tenantId: string,
    ifMatch: string,
  ): Promise<PlanDefinition> {
    return this.updateResource('PlanDefinition', resource, tenantId, ifMatch);
  }

  getActivityDefinition(
    id: string,
    tenantId: string,
  ): Promise<ActivityDefinition> {
    return this.readResource('ActivityDefinition', id, tenantId);
  }
  searchActivityDefinitions(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<ActivityDefinition>> {
    return this.searchResources('ActivityDefinition', tenantId, params);
  }
  createActivityDefinition(
    resource: ActivityDefinition,
    tenantId: string,
  ): Promise<ActivityDefinition> {
    return this.createResource('ActivityDefinition', resource, tenantId);
  }
  updateActivityDefinition(
    resource: ActivityDefinition,
    tenantId: string,
    ifMatch: string,
  ): Promise<ActivityDefinition> {
    return this.updateResource(
      'ActivityDefinition',
      resource,
      tenantId,
      ifMatch,
    );
  }

  getQuestionnaire(id: string, tenantId: string): Promise<Questionnaire> {
    return this.readResource('Questionnaire', id, tenantId);
  }
  searchQuestionnaires(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<Questionnaire>> {
    return this.searchResources('Questionnaire', tenantId, params);
  }
  createQuestionnaire(
    resource: Questionnaire,
    tenantId: string,
  ): Promise<Questionnaire> {
    return this.createResource('Questionnaire', resource, tenantId);
  }
  updateQuestionnaire(
    resource: Questionnaire,
    tenantId: string,
    ifMatch: string,
  ): Promise<Questionnaire> {
    return this.updateResource('Questionnaire', resource, tenantId, ifMatch);
  }

  getLibrary(id: string, tenantId: string): Promise<Library> {
    return this.readResource('Library', id, tenantId);
  }
  searchLibraries(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<Library>> {
    return this.searchResources('Library', tenantId, params);
  }
  createLibrary(resource: Library, tenantId: string): Promise<Library> {
    return this.createResource('Library', resource, tenantId);
  }
  updateLibrary(
    resource: Library,
    tenantId: string,
    ifMatch: string,
  ): Promise<Library> {
    return this.updateResource('Library', resource, tenantId, ifMatch);
  }

  getCarePlan(id: string, tenantId: string): Promise<CarePlan> {
    return this.readResource('CarePlan', id, tenantId);
  }
  searchCarePlans(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<CarePlan>> {
    return this.searchResources('CarePlan', tenantId, params);
  }
  createCarePlan(resource: CarePlan, tenantId: string): Promise<CarePlan> {
    return this.createResource('CarePlan', resource, tenantId);
  }
  updateCarePlan(
    resource: CarePlan,
    tenantId: string,
    ifMatch: string,
  ): Promise<CarePlan> {
    return this.updateResource('CarePlan', resource, tenantId, ifMatch);
  }

  getTask(id: string, tenantId: string): Promise<Task> {
    return this.readResource('Task', id, tenantId);
  }
  searchTasks(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<Task>> {
    return this.searchResources('Task', tenantId, params);
  }
  createTask(resource: Task, tenantId: string): Promise<Task> {
    return this.createResource('Task', resource, tenantId);
  }
  updateTask(
    resource: Task,
    tenantId: string,
    ifMatch: string,
  ): Promise<Task> {
    return this.updateResource('Task', resource, tenantId, ifMatch);
  }

  getQuestionnaireResponse(
    id: string,
    tenantId: string,
  ): Promise<QuestionnaireResponse> {
    return this.readResource('QuestionnaireResponse', id, tenantId);
  }
  searchQuestionnaireResponses(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<QuestionnaireResponse>> {
    return this.searchResources('QuestionnaireResponse', tenantId, params);
  }
  createQuestionnaireResponse(
    resource: QuestionnaireResponse,
    tenantId: string,
  ): Promise<QuestionnaireResponse> {
    return this.createResource('QuestionnaireResponse', resource, tenantId);
  }

  getObservation(id: string, tenantId: string): Promise<Observation> {
    return this.readResource('Observation', id, tenantId);
  }
  searchObservations(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<Observation>> {
    return this.searchResources('Observation', tenantId, params);
  }
  createObservation(
    resource: Observation,
    tenantId: string,
  ): Promise<Observation> {
    return this.createResource('Observation', resource, tenantId);
  }

  getProvenance(id: string, tenantId: string): Promise<Provenance> {
    return this.readResource('Provenance', id, tenantId);
  }
  createProvenance(
    resource: Provenance,
    tenantId: string,
  ): Promise<Provenance> {
    return this.createResource('Provenance', resource, tenantId);
  }

  getAuditEvent(id: string, tenantId: string): Promise<AuditEvent> {
    return this.readResource('AuditEvent', id, tenantId);
  }
  createAuditEvent(
    resource: AuditEvent,
    tenantId: string,
  ): Promise<AuditEvent> {
    return this.createResource('AuditEvent', resource, tenantId);
  }
}
